import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const requirements = [
    'At least 8 characters long',
    'Contains letters (a-z, A-Z)',
    'Contains numbers (0-9)',
    'Contains special characters (!@#$%^&* etc.)'
  ];

  const validatePasswordStrength = (password: string) => {
    const checks = {
      length: password.length >= 8,
      letters: /[a-zA-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    return checks;
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    const checks = validatePasswordStrength(newPassword);
    if (!checks.length || !checks.letters || !checks.numbers || !checks.special) {
      setMessage({ type: 'error', text: 'Password does not meet all requirements' });
      return;
    }

    setLoading(true);

    try {
      await api.post('/password/change', {
        currentPassword,
        newPassword
      });

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to change password';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const passwordChecks = newPassword ? validatePasswordStrength(newPassword) : null;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-primary-500/20 bg-slate-900/95 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">⚙️ Account Settings</h1>
          <p className="text-slate-400">Manage your account preferences and security</p>
        </div>

        {/* User Info */}
        <div className="card mb-8">
          <h2 className="text-xl font-bold mb-4">Profile Information</h2>
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-slate-400">Name:</span>
              <span className="ml-2 font-semibold">{user?.firstName} {user?.lastName}</span>
            </div>
            <div>
              <span className="text-slate-400">Email:</span>
              <span className="ml-2 font-semibold">{user?.email}</span>
            </div>
            <div>
              <span className="text-slate-400">Role:</span>
              <span className="ml-2 font-semibold capitalize">{user?.role}</span>
            </div>
          </div>
        </div>

        {/* Change Password */}
        <div className="card">
          <h2 className="text-xl font-bold mb-4">Change Password</h2>
          
          {message && (
            <div className={`mb-4 p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
                : 'bg-red-500/20 border border-red-500/30 text-red-300'
            }`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                placeholder="Confirm new password"
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-sm font-semibold mb-2">Password Requirements:</div>
              <ul className="space-y-1 text-sm">
                {passwordChecks ? (
                  <>
                    <li className={passwordChecks.length ? 'text-green-400' : 'text-slate-400'}>
                      {passwordChecks.length ? '✓' : '○'} At least 8 characters long
                    </li>
                    <li className={passwordChecks.letters ? 'text-green-400' : 'text-slate-400'}>
                      {passwordChecks.letters ? '✓' : '○'} Contains letters (a-z, A-Z)
                    </li>
                    <li className={passwordChecks.numbers ? 'text-green-400' : 'text-slate-400'}>
                      {passwordChecks.numbers ? '✓' : '○'} Contains numbers (0-9)
                    </li>
                    <li className={passwordChecks.special ? 'text-green-400' : 'text-slate-400'}>
                      {passwordChecks.special ? '✓' : '○'} Contains special characters (!@#$%^&* etc.)
                    </li>
                  </>
                ) : (
                  requirements.map((req, idx) => (
                    <li key={idx} className="text-slate-400">○ {req}</li>
                  ))
                )}
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
